import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useSocketEvent } from '../hooks/useSocket';
import api from '../services/api';
import toast from 'react-hot-toast';
import { AppleEmoji } from '../components/Emoji';
import { useAuth } from '../contexts/AuthContext';
import {
  IconPlus, IconEdit, IconTrash, IconX, IconUpload,
  IconBuilding,
  IconInstagram, IconFacebook, IconTikTok,
  IconCheck, IconSparkles, IconRefresh, IconCalendar,
  IconChevronLeft, IconChevronRight
} from '../components/Icons';

const PIECE_TYPES = [
  { key: 'Estática', label: 'Estáticas', icon: '🖼️' },
  { key: 'Animación', label: 'Animaciones', icon: '✨' },
  { key: 'Video', label: 'Videos', icon: '🎬' },
  { key: 'Carrusel', label: 'Carruseles', icon: '📑' },
  { key: 'Cuadro de Material', label: 'Cuadro de Material', icon: '📦' },
];

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const NETWORKS = [
  { key: 'instagram', label: 'Instagram', color: '#E1306C', icon: IconInstagram },
  { key: 'facebook', label: 'Facebook', color: '#1877F2', icon: IconFacebook },
  { key: 'tiktok', label: 'TikTok', color: '#010101', icon: IconTikTok },
];

const glassInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: '12px',
  border: '1px solid rgba(203, 213, 225, 0.8)',
  background: 'rgba(255, 255, 255, 0.8)',
  color: '#0f2a4a',
  fontSize: '13px',
  outline: 'none',
  fontFamily: '"Montserrat", sans-serif',
  boxSizing: 'border-box',
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.03)',
  transition: 'border-color 0.2s ease'
};

const glassLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '10px',
  fontWeight: 800,
  color: 'rgba(15, 42, 74, 0.55)',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  marginBottom: '6px'
};

const mesActual = new Date().toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });

export const Brands: React.FC = () => {
  const [brands, setBrands] = useState<any[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'scope'>('info');
  const [scopeItems, setScopeItems] = useState<any[]>([]);
  const [savingScope, setSavingScope] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const editLogoRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const { user } = useAuth();
  const isAdminOrAccounts = ['ADMIN', 'CUENTAS'].includes(user?.role || '');

  // Estados Módulo 3: Parámetros Mensuales de Conteos
  const [showMonthlyScopeModal, setShowMonthlyScopeModal] = useState(false);
  const [matrixMonth, setMatrixMonth] = useState(new Date().getMonth() + 1);
  const [matrixYear, setMatrixYear] = useState(new Date().getFullYear());
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixData, setMatrixData] = useState<{ summary?: any; matrix: any[] }>({ matrix: [] });
  const [editableMatrix, setEditableMatrix] = useState<Record<string, Record<string, number>>>({});
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [copyingPrevious, setCopyingPrevious] = useState(false);
  const [savingBrandRowId, setSavingBrandRowId] = useState<string | null>(null);

  // Estados de fecha para modal detalle de marca
  const [detailScopeMonth, setDetailScopeMonth] = useState(new Date().getMonth() + 1);
  const [detailScopeYear, setDetailScopeYear] = useState(new Date().getFullYear());

  const [form, setForm] = useState({
    name: '',
    responsableId: '',
    logo: '',
    networks: { instagram: false, facebook: false, tiktok: false },
    scopeItems: PIECE_TYPES.map(t => ({ tipo: t.key, cantidad: 0 }))
  });
  const [editForm, setEditForm] = useState({
    name: '',
    responsableId: '',
    logo: '',
    networks: { instagram: false, facebook: false, tiktok: false },
    active: true
  });

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [br, ur] = await Promise.all([api.get('/brands'), api.get('/users')]);
      setBrands(Array.isArray(br.data) ? br.data : []);
      setTeam((Array.isArray(ur.data) ? ur.data : []).filter((u: any) => u.role !== 'CLIENTE'));
    } catch {} finally {
      setLoading(false);
    }
  };

  useSocketEvent('refresh:brands', load);

  const loadScope = async (brandId: string, m = detailScopeMonth, y = detailScopeYear) => {
    try {
      const r = await api.get(`/brands/${brandId}/scope?mes=${m}&anio=${y}`);
      const data = Array.isArray(r.data) ? r.data : [];
      setScopeItems(PIECE_TYPES.map(pt => {
        const ex = data.find((s: any) => s.tipo === pt.key);
        return ex || { tipo: pt.key, cantidad: 0, completados: 0 };
      }));
    } catch {}
  };

  const loadMonthlyMatrix = async (m = matrixMonth, y = matrixYear) => {
    setMatrixLoading(true);
    try {
      const res = await api.get(`/brands/scope/monthly-matrix?mes=${m}&anio=${y}`);
      setMatrixData(res.data || { matrix: [] });
      const initial: Record<string, Record<string, number>> = {};
      if (res.data?.matrix) {
        res.data.matrix.forEach((b: any) => {
          initial[b.brandId] = {};
          b.items.forEach((it: any) => {
            initial[b.brandId][it.tipo] = it.cantidad;
          });
        });
      }
      setEditableMatrix(initial);
    } catch (err: any) {
      toast.error('Error al cargar parámetros mensuales de conteo');
    } finally {
      setMatrixLoading(false);
    }
  };

  const openMonthlyMatrixModal = () => {
    setShowMonthlyScopeModal(true);
    loadMonthlyMatrix(matrixMonth, matrixYear);
  };

  const handleCopyPreviousMonth = async () => {
    if (!window.confirm(`¿Deseas duplicar los parámetros de conteo del mes anterior para ${MONTH_NAMES[matrixMonth - 1]} ${matrixYear}? Se reemplazarán las metas no guardadas de este periodo.`)) {
      return;
    }
    setCopyingPrevious(true);
    try {
      const res = await api.post('/brands/scope/copy-previous', {
        targetMes: matrixMonth,
        targetAnio: matrixYear
      });
      toast.success(res.data?.message || 'Parámetros duplicados con éxito');
      await loadMonthlyMatrix(matrixMonth, matrixYear);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al duplicar parámetros del mes anterior');
    } finally {
      setCopyingPrevious(false);
    }
  };

  const handleSaveBrandScopeRow = async (brandId: string) => {
    const brandValues = editableMatrix[brandId] || {};
    const items = Object.entries(brandValues).map(([tipo, cantidad]) => ({
      tipo,
      cantidad: Number(cantidad) || 0
    }));

    setSavingBrandRowId(brandId);
    try {
      await api.post(`/brands/${brandId}/scope`, {
        mes: matrixMonth,
        anio: matrixYear,
        items
      });
      toast.success('Parámetros guardados con éxito');
      await loadMonthlyMatrix(matrixMonth, matrixYear);
      load();
    } catch (err: any) {
      toast.error('Error al guardar parámetros de la marca');
    } finally {
      setSavingBrandRowId(null);
    }
  };

  const handleSaveAllMatrix = async () => {
    const updates = Object.entries(editableMatrix).map(([brandId, values]) => ({
      brandId,
      items: Object.entries(values).map(([tipo, cantidad]) => ({
        tipo,
        cantidad: Number(cantidad) || 0
      }))
    }));

    setSavingMatrix(true);
    try {
      await api.post('/brands/scope/monthly-matrix', {
        mes: matrixMonth,
        anio: matrixYear,
        updates
      });
      toast.success('Todos los parámetros mensuales han sido guardados');
      await loadMonthlyMatrix(matrixMonth, matrixYear);
      load();
    } catch (err: any) {
      toast.error('Error al guardar matriz de parámetros');
    } finally {
      setSavingMatrix(false);
    }
  };

  const uploadLogo = async (file: File, forEdit = false) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (forEdit) setEditForm(p => ({ ...p, logo: r.data.url }));
      else setForm(p => ({ ...p, logo: r.data.url }));
      toast.success('Logo subido');
    } catch {
      toast.error('Error al subir logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Ingresa el nombre'); return; }
    setSaving(true);
    try {
      const clientId = team[0]?.id;
      await api.post('/brands', {
        name: form.name,
        clientId,
        responsableId: form.responsableId || null,
        logo: form.logo || null,
        networks: form.networks,
        scopeItems: form.scopeItems.filter(s => s.cantidad > 0)
      });
      toast.success('Marca creada');
      setShowModal(false);
      setForm({
        name: '',
        responsableId: '',
        logo: '',
        networks: { instagram: false, facebook: false, tiktok: false },
        scopeItems: PIECE_TYPES.map(t => ({ tipo: t.key, cantidad: 0 }))
      });
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBrand) return;
    setSavingEdit(true);
    try {
      await api.put(`/brands/${selectedBrand.id}`, {
        name: editForm.name,
        responsableId: editForm.responsableId || null,
        logo: editForm.logo || null,
        networks: editForm.networks,
        active: editForm.active
      });
      toast.success('Marca actualizada');
      setShowEdit(false);
      setShowDetail(false);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBrand) return;
    if (!window.confirm(`¿Eliminar "${selectedBrand.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/brands/${selectedBrand.id}`);
      toast.success('Marca eliminada');
      setShowDetail(false);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error');
    }
  };

  const openDetail = async (brand: any) => {
    setSelectedBrand(brand);
    setActiveTab('info');
    setShowDetail(true);
    await loadScope(brand.id);
  };

  const openEdit = () => {
    if (!selectedBrand) return;
    setEditForm({
      name: selectedBrand.name,
      responsableId: selectedBrand.responsableId || '',
      logo: selectedBrand.logo || '',
      networks: selectedBrand.networks || { instagram: false, facebook: false, tiktok: false },
      active: selectedBrand.active
    });
    setShowEdit(true);
  };

  const handleSaveScope = async () => {
    if (!selectedBrand) return;
    setSavingScope(true);
    try {
      await api.post(`/brands/${selectedBrand.id}/scope`, {
        mes: detailScopeMonth,
        anio: detailScopeYear,
        items: scopeItems.filter(s => s.cantidad > 0)
      });
      toast.success('Entregables guardados con éxito');
      load();
    } catch {
      toast.error('Error');
    } finally {
      setSavingScope(false);
    }
  };

  return (
    <div className="zoik-page-container" style={{ display: 'flex', height: '100vh', maxHeight: '100vh', width: '100vw', maxWidth: '100vw', overflow: 'hidden', background: 'radial-gradient(at 10% 10%, rgba(224, 242, 254, 0.4) 0px, transparent 50%), radial-gradient(at 90% 90%, rgba(241, 245, 249, 0.7) 0px, transparent 50%), #f8fafc', fontFamily: '"Montserrat", sans-serif' }}>
      {/* ESTILOS INLINE PARA LIQUID GLASS, DESPLIEGUE ACTIVA Y ANIMACIONES */}
      <style>{`
        @keyframes glassModalEnter {
          0% {
            opacity: 0;
            transform: scale(0.92) translateY(16px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes socialIconSlideIn {
          0% {
            opacity: 0;
            transform: translateX(24px) scale(0.85);
          }
          65% {
            transform: translateX(-2px) scale(1.04);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes activePillSequence {
          0% {
            width: 78px;
            padding: 4px 10px;
            opacity: 0;
            transform: translateX(18px);
          }
          15% {
            width: 78px;
            padding: 4px 10px;
            opacity: 1;
            transform: translateX(0);
          }
          70% {
            width: 78px;
            padding: 4px 10px;
            opacity: 1;
          }
          100% {
            width: 24px;
            padding: 4px;
            opacity: 1;
          }
        }
        @keyframes activeTextSequence {
          0%, 65% {
            opacity: 1;
            max-width: 50px;
            margin-left: 6px;
          }
          85%, 100% {
            opacity: 0;
            max-width: 0px;
            margin-left: 0px;
            overflow: hidden;
          }
        }
        @keyframes pulseBlinkGreen {
          0%, 100% {
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.75);
            opacity: 0.85;
          }
          50% {
            transform: scale(1.15);
            box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
            opacity: 1;
          }
        }
        .liquid-glass-card {
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.65);
          box-shadow: 0 8px 32px 0 rgba(15, 42, 74, 0.04), inset 0 1.5px 2px rgba(255, 255, 255, 0.85), inset 0 -1px 2px rgba(0, 0, 0, 0.02);
          transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .liquid-glass-card:hover {
          background: rgba(255, 255, 255, 0.5);
          transform: translateY(-4px);
          box-shadow: 0 16px 40px -4px rgba(15, 42, 74, 0.09), inset 0 1.5px 2px rgba(255, 255, 255, 0.95);
          border-color: rgba(0, 112, 243, 0.4);
        }
        .liquid-glass-modal {
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          border: 1px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 25px 60px -12px rgba(15, 42, 74, 0.22), inset 0 1.5px 2px rgba(255, 255, 255, 1);
          animation: glassModalEnter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .social-glass-btn {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.7);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(15, 42, 74, 0.04), inset 0 1px 1.5px rgba(255, 255, 255, 0.85);
          transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          animation: socialIconSlideIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) backwards;
          cursor: default;
        }
        .social-glass-btn:hover {
          transform: translateY(-2px) scale(1.12);
          background: rgba(255, 255, 255, 0.8);
          box-shadow: 0 4px 12px rgba(15, 42, 74, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 1);
        }
        .active-status-bubble {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 24px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.3);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-sizing: border-box;
          animation: activePillSequence 2.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          overflow: hidden;
          cursor: pointer;
          transition: width 0.3s ease, padding 0.3s ease;
          white-space: nowrap;
        }
        .active-status-bubble:hover {
          width: 78px !important;
          padding: 4px 10px !important;
        }
        .active-status-bubble:hover .active-label-text {
          opacity: 1 !important;
          max-width: 50px !important;
          margin-left: 6px !important;
        }
        .active-dot-pulsing {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          flex-shrink: 0;
          animation: pulseBlinkGreen 1.6s ease-in-out infinite;
        }
        .active-label-text {
          color: #10b981;
          font-size: 11px;
          font-weight: 800;
          animation: activeTextSequence 2.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          transition: opacity 0.25s ease, max-width 0.25s ease, margin-left 0.25s ease;
          display: inline-block;
        }
      `}</style>

      <Sidebar />

      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        padding: '36px 40px',
        overflowY: 'auto',
        overflowX: 'hidden' }}>
        {/* CABECERA DE MARCAS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(0, 112, 243, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AppleEmoji symbol="🏢" size={20} />
              </div>
              <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 900, color: '#0f2a4a' }}>Marcas</h1>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'rgba(15, 42, 74, 0.5)', fontWeight: 600 }}>
              Entregables, redes sociales y progreso mensual por cliente
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isAdminOrAccounts && (
              <button
                onClick={openMonthlyMatrixModal}
                style={{
                  padding: '11px 20px',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 112, 243, 0.3)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(16px)',
                  color: '#0070f3',
                  fontWeight: 800,
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(0, 112, 243, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.9)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <AppleEmoji symbol="⚙️" size={16} />
                <span>Parámetros Mensuales de Conteos</span>
              </button>
            )}

            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '11px 22px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                background: 'rgba(0, 112, 243, 0.95)',
                backdropFilter: 'blur(12px)',
                color: 'white',
                fontWeight: 800,
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: '"Montserrat", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.35)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <IconPlus size={16} color="#ffffff" />
              <span>Nueva marca</span>
            </button>
          </div>
        </div>

        {/* LISTADO DE MARCAS EN CUADRÍCULA LIQUID GLASS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {loading ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
              Cargando marcas...
            </div>
          ) : brands.length === 0 ? (
            <div
              className="liquid-glass-card"
              style={{
                gridColumn: '1/-1',
                textAlign: 'center',
                padding: '60px 24px',
                borderRadius: '20px',
                color: '#64748b'
              }}
            >
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(0, 112, 243, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <IconBuilding size={28} color="#0070f3" />
              </div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f2a4a' }}>No hay marcas registradas aún</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>Crea tu primera marca con el botón superior.</div>
            </div>
          ) : (
            brands.map(b => {
              const sc = b.scope || [];
              const tot = sc.reduce((s: number, i: any) => s + i.cantidad, 0);
              const comp = sc.reduce((s: number, i: any) => s + (i.completados || 0), 0);
              const pct = tot > 0 ? Math.round((comp / tot) * 100) : 0;
              const pctColor = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#0070f3';
              const nets = b.networks || {};

              return (
                <div
                  key={b.id}
                  onClick={() => openDetail(b)}
                  className="liquid-glass-card"
                  style={{
                    borderRadius: '20px',
                    padding: '22px',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  {/* Encabezado de la Tarjeta (Logo + Badge Activo con animación que se cierra a círculo titilante) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                    {b.logo ? (
                      <img
                        src={b.logo}
                        alt={b.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '14px',
                          objectFit: 'cover',
                          border: '1px solid rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 3px 10px rgba(15, 42, 74, 0.08)'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '14px',
                          background: 'linear-gradient(135deg, rgba(0, 112, 243, 0.15), rgba(0, 112, 243, 0.05))',
                          border: '1px solid rgba(0, 112, 243, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: 900,
                          color: '#0070f3',
                          boxShadow: '0 3px 10px rgba(0, 112, 243, 0.08)'
                        }}
                      >
                        {b.name[0]?.toUpperCase()}
                      </div>
                    )}

                    {/* Badge de Estado: Animación de despliegue y cierre a punto verde titilante */}
                    {b.active ? (
                      <div className="active-status-bubble" title="Marca Activa">
                        <span className="active-dot-pulsing" />
                        <span className="active-label-text">Activa</span>
                      </div>
                    ) : (
                      <span
                        style={{
                          background: 'rgba(100, 116, 139, 0.12)',
                          color: '#64748b',
                          border: '1px solid rgba(100, 116, 139, 0.25)',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 800,
                          backdropFilter: 'blur(8px)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#64748b' }} />
                        <span>Inactiva</span>
                      </span>
                    )}
                  </div>

                  {/* Nombre y Responsable */}
                  <div style={{ fontWeight: 900, fontSize: '16px', color: '#0f2a4a', marginBottom: '3px', lineHeight: '1.3' }}>
                    {b.name}
                  </div>
                  {b.responsable ? (
                    <div style={{ fontSize: '12px', color: '#0070f3', fontWeight: 700, marginBottom: '10px' }}>
                      {b.responsable.firstName} {b.responsable.lastName}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, marginBottom: '10px' }}>
                      Sin responsable asignado
                    </div>
                  )}

                  {/* Redes Sociales: Botones Liquid Glass SOLO CON EL ICONO EN TRAZO y animación de deslizamiento */}
                  {(nets.instagram || nets.facebook || nets.tiktok) && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                      {NETWORKS.filter(n => nets[n.key]).map((n, idx) => {
                        const IconComponent = n.icon;
                        return (
                          <div
                            key={n.key}
                            className="social-glass-btn"
                            title={n.label}
                            style={{
                              color: n.color,
                              animationDelay: `${idx * 0.09}s`
                            }}
                          >
                            <IconComponent size={15} color={n.color} />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tipos de Entregables */}
                  {sc.filter((s: any) => s.cantidad > 0).length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
                      {sc.filter((s: any) => s.cantidad > 0).map((s: any) => {
                        const pt = PIECE_TYPES.find(p => p.key === s.tipo);
                        return (
                          <div
                            key={s.tipo}
                            style={{
                              fontSize: '11px',
                              padding: '3px 8px',
                              borderRadius: '8px',
                              background: 'rgba(255, 255, 255, 0.4)',
                              border: '1px solid rgba(255, 255, 255, 0.6)',
                              color: '#0f2a4a',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {pt?.icon && <AppleEmoji symbol={pt.icon} size={12} />}
                            <span>{s.tipo}: {s.completados || 0}/{s.cantidad}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Barra de Progreso Mensual */}
                  {tot > 0 ? (
                    <div style={{ paddingTop: '12px', borderTop: '1px solid rgba(226, 232, 240, 0.7)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'capitalize' }}>
                          {mesActual}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 900, color: pctColor }}>
                          {pct}%
                        </span>
                      </div>
                      <div
                        style={{
                          background: 'rgba(15, 42, 74, 0.06)',
                          borderRadius: '6px',
                          height: '7px',
                          overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.5)'
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${pct}%`,
                            background: pctColor,
                            borderRadius: '6px',
                            transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            boxShadow: `0 1px 4px ${pctColor}66`
                          }}
                        />
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', fontWeight: 600 }}>
                        {b._count?.pieces || 0} piezas creadas
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#94a3b8', paddingTop: '10px', borderTop: '1px solid rgba(226, 232, 240, 0.7)', fontWeight: 600 }}>
                      Sin entregables definidos
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      {/* MODAL MÓDULO 3: REVISIÓN Y ACTUALIZACIÓN DE PARÁMETROS MENSUALES DE CONTEOS */}
      {showMonthlyScopeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.55)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '24px'
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowMonthlyScopeModal(false); }}
        >
          <div
            className="liquid-glass-modal"
            style={{
              width: '100%',
              maxWidth: '1260px',
              maxHeight: '92vh',
              borderRadius: '26px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 30px 80px -15px rgba(15, 42, 74, 0.3), inset 0 1.5px 2px rgba(255, 255, 255, 1)'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '22px 28px',
                borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.85) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(0, 112, 243, 0.3)',
                    color: '#ffffff'
                  }}
                >
                  <AppleEmoji symbol="⚙️" size={24} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#0f2a4a', letterSpacing: '-0.3px' }}>
                    Parámetros Mensuales de Conteos y Entregables
                  </h2>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                    Revisión y configuración de cuotas de entrega por marca, conteos de unidades reales y avance mensual
                  </p>
                </div>
              </div>

              {/* Controles de Mes, Año y Acciones */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '4px 8px',
                    borderRadius: '14px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)'
                  }}
                >
                  <button
                    onClick={() => {
                      let nextM = matrixMonth - 1;
                      let nextY = matrixYear;
                      if (nextM < 1) { nextM = 12; nextY -= 1; }
                      setMatrixMonth(nextM);
                      setMatrixYear(nextY);
                      loadMonthlyMatrix(nextM, nextY);
                    }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 6px', color: '#0f2a4a', borderRadius: '6px' }}
                    title="Mes anterior"
                  >
                    <IconChevronLeft size={16} />
                  </button>

                  <select
                    value={matrixMonth}
                    onChange={e => {
                      const m = Number(e.target.value);
                      setMatrixMonth(m);
                      loadMonthlyMatrix(m, matrixYear);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      fontWeight: 800,
                      fontSize: '13px',
                      color: '#0f2a4a',
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      padding: '4px 6px'
                    }}
                  >
                    {MONTH_NAMES.map((name, idx) => (
                      <option key={idx} value={idx + 1}>{name}</option>
                    ))}
                  </select>

                  <select
                    value={matrixYear}
                    onChange={e => {
                      const y = Number(e.target.value);
                      setMatrixYear(y);
                      loadMonthlyMatrix(matrixMonth, y);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      fontWeight: 800,
                      fontSize: '13px',
                      color: '#0f2a4a',
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      padding: '4px 6px'
                    }}
                  >
                    {[2025, 2026, 2027].map(yr => (
                      <option key={yr} value={yr}>{yr}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => {
                      let nextM = matrixMonth + 1;
                      let nextY = matrixYear;
                      if (nextM > 12) { nextM = 1; nextY += 1; }
                      setMatrixMonth(nextM);
                      setMatrixYear(nextY);
                      loadMonthlyMatrix(nextM, nextY);
                    }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 6px', color: '#0f2a4a', borderRadius: '6px' }}
                    title="Mes siguiente"
                  >
                    <IconChevronRight size={16} />
                  </button>
                </div>

                <button
                  onClick={handleCopyPreviousMonth}
                  disabled={copyingPrevious || matrixLoading}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#059669',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: copyingPrevious ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                  title="Duplica los parámetros configurados del mes previo para no comenzar desde cero"
                >
                  <IconSparkles size={14} color="#059669" />
                  <span>{copyingPrevious ? 'Duplicando...' : 'Copiar del Mes Anterior'}</span>
                </button>

                <button
                  onClick={handleSaveAllMatrix}
                  disabled={savingMatrix || matrixLoading}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: savingMatrix ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <IconCheck size={14} color="#ffffff" />
                  <span>{savingMatrix ? 'Guardando...' : 'Guardar Todo'}</span>
                </button>

                <button
                  onClick={() => setShowMonthlyScopeModal(false)}
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: '1px solid rgba(203, 213, 225, 0.7)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  <IconX size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '22px 28px' }}>
              {/* KPI Summary Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '22px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: '16px', padding: '16px 18px', border: '1px solid rgba(226, 232, 240, 0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Meta Total Programada
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#0f2a4a', marginTop: '4px' }}>
                    {matrixData.summary?.globalTargetUnits || 0} <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b' }}>unidades</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Sumatoria de cuotas de todas las marcas
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: '16px', padding: '16px 18px', border: '1px solid rgba(226, 232, 240, 0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Unidades Aprobadas en el Mes
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#059669', marginTop: '4px' }}>
                    {matrixData.summary?.globalCompletedUnits || 0} <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b' }}>unidades</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Conteos reales (slides de carrusel incluidos)
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: '16px', padding: '16px 18px', border: '1px solid rgba(226, 232, 240, 0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Cumplimiento Global
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: (matrixData.summary?.overallProgress || 0) >= 80 ? '#059669' : (matrixData.summary?.overallProgress || 0) >= 50 ? '#d97706' : '#0070f3', marginTop: '4px' }}>
                    {matrixData.summary?.overallProgress || 0}%
                  </div>
                  <div style={{ height: '6px', width: '100%', background: '#e2e8f0', borderRadius: '3px', marginTop: '8px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(100, matrixData.summary?.overallProgress || 0)}%`,
                        background: (matrixData.summary?.overallProgress || 0) >= 80 ? '#10b981' : (matrixData.summary?.overallProgress || 0) >= 50 ? '#f59e0b' : '#0070f3',
                        borderRadius: '3px',
                        transition: 'width 0.4s ease'
                      }}
                    />
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: '16px', padding: '16px 18px', border: '1px solid rgba(226, 232, 240, 0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Marcas Configuradas
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#0f2a4a', marginTop: '4px' }}>
                    {matrixData.summary?.configuredBrands || 0} <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b' }}>/ {matrixData.summary?.totalBrands || 0}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: (matrixData.summary?.unconfiguredBrands || 0) > 0 ? '#d97706' : '#059669', marginTop: '4px', fontWeight: 700 }}>
                    {(matrixData.summary?.unconfiguredBrands || 0) > 0 ? `${matrixData.summary?.unconfiguredBrands} marcas sin metas este mes` : '100% de marcas con metas'}
                  </div>
                </div>
              </div>

              {/* Table of Brands and Formats */}
              {matrixLoading ? (
                <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontWeight: 700 }}>
                  <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #0070f3', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '12px' }} />
                  <div>Cargando matriz mensual de parámetros...</div>
                </div>
              ) : (
                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.85)',
                    borderRadius: '18px',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#0f2a4a', color: '#ffffff' }}>
                        <th style={{ padding: '12px 16px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>Marca & Cliente</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>🖼️ Estática</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>🎬 Video</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>📑 Carrusel</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>✨ Animación</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>📦 Cuadro Material</th>
                        <th style={{ padding: '12px 12px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>Meta Total</th>
                        <th style={{ padding: '12px 12px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>Aprobadas</th>
                        <th style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>Cumplimiento</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10.5px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixData.matrix?.map((row: any, rIdx: number) => {
                        const brandValues = editableMatrix[row.brandId] || {};
                        const currentSum = Object.values(brandValues).reduce((a, b) => a + (Number(b) || 0), 0);
                        const rowPct = currentSum > 0 ? Math.round((row.totalCompleted / currentSum) * 100) : 0;
                        const isSavingThis = savingBrandRowId === row.brandId;

                        return (
                          <tr
                            key={row.brandId}
                            style={{
                              borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
                              background: rIdx % 2 === 0 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(248, 250, 252, 0.6)',
                              transition: 'background 0.15s ease'
                            }}
                          >
                            {/* Marca */}
                            <td style={{ padding: '14px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {row.logo ? (
                                  <img src={row.logo} alt={row.brandName} style={{ width: '30px', height: '30px', borderRadius: '8px', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#0f2a4a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '12px' }}>
                                    {row.brandName.charAt(0)}
                                  </div>
                                )}
                                <div>
                                  <div style={{ fontWeight: 800, color: '#0f2a4a', fontSize: '13px' }}>{row.brandName}</div>
                                  <div style={{ fontSize: '10.5px', color: '#64748b', fontWeight: 600 }}>
                                    {row.responsable ? `${row.responsable.firstName} ${row.responsable.lastName}` : 'Sin responsable'}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Inputs por Formato */}
                            {['Estática', 'Video', 'Carrusel', 'Animación', 'Cuadro de Material'].map(fmt => {
                              const val = brandValues[fmt] !== undefined ? brandValues[fmt] : 0;
                              return (
                                <td key={fmt} style={{ padding: '8px 6px', textAlign: 'center' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={val}
                                    onChange={e => {
                                      const num = Math.max(0, parseInt(e.target.value) || 0);
                                      setEditableMatrix(prev => ({
                                        ...prev,
                                        [row.brandId]: {
                                          ...(prev[row.brandId] || {}),
                                          [fmt]: num
                                        }
                                      }));
                                    }}
                                    style={{
                                      width: '60px',
                                      padding: '6px',
                                      textAlign: 'center',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(203, 213, 225, 0.8)',
                                      background: val > 0 ? '#ffffff' : '#f8fafc',
                                      fontSize: '13px',
                                      fontWeight: 800,
                                      color: val > 0 ? '#0f2a4a' : '#94a3b8',
                                      outline: 'none',
                                      fontFamily: '"Montserrat", sans-serif'
                                    }}
                                  />
                                </td>
                              );
                            })}

                            {/* Meta Total */}
                            <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 900, fontSize: '14px', color: currentSum > 0 ? '#0f2a4a' : '#94a3b8' }}>
                                {currentSum}
                              </span>
                            </td>

                            {/* Aprobadas */}
                            <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 900, fontSize: '14px', color: row.totalCompleted > 0 ? '#059669' : '#64748b' }}>
                                {row.totalCompleted}
                              </span>
                            </td>

                            {/* Avance */}
                            <td style={{ padding: '14px 14px', textAlign: 'center' }}>
                              <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: '70px' }}>
                                <span
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    background: rowPct >= 80 ? 'rgba(16, 185, 129, 0.15)' : rowPct >= 50 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(0, 112, 243, 0.12)',
                                    color: rowPct >= 80 ? '#059669' : rowPct >= 50 ? '#d97706' : '#0070f3'
                                  }}
                                >
                                  {rowPct}%
                                </span>
                                <div style={{ height: '4px', width: '55px', background: '#e2e8f0', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                                  <div
                                    style={{
                                      height: '100%',
                                      width: `${Math.min(100, rowPct)}%`,
                                      background: rowPct >= 80 ? '#10b981' : rowPct >= 50 ? '#f59e0b' : '#0070f3',
                                      borderRadius: '2px'
                                    }}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* Acción */}
                            <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleSaveBrandScopeRow(row.brandId)}
                                disabled={isSavingThis}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(0, 112, 243, 0.3)',
                                  background: 'rgba(0, 112, 243, 0.08)',
                                  color: '#0070f3',
                                  fontWeight: 800,
                                  fontSize: '11px',
                                  cursor: isSavingThis ? 'not-allowed' : 'pointer',
                                  fontFamily: '"Montserrat", sans-serif',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                {isSavingThis ? 'Guardando...' : 'Guardar'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </main>

      {/* MODAL VENTANA GLASS: CREAR MARCA */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.45)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div
            className="liquid-glass-modal"
            style={{
              borderRadius: '24px',
              padding: '32px',
              width: '100%',
              maxWidth: '540px',
              maxHeight: '88vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(0, 112, 243, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AppleEmoji symbol="🏢" size={18} />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>Nueva Marca</h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  color: '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconX size={16} />
              </button>
            </div>

            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gap: '18px' }}>
                <div>
                  <label style={glassLabelStyle}>Logo de la Marca</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      onClick={() => logoInputRef.current?.click()}
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '16px',
                        border: '2px dashed rgba(0, 112, 243, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        background: 'rgba(255, 255, 255, 0.8)',
                        flexShrink: 0
                      }}
                    >
                      {form.logo ? (
                        <img src={form.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <AppleEmoji symbol="📷" size={24} />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '10px',
                        border: '1px solid rgba(203, 213, 225, 0.8)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#0f2a4a',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: '"Montserrat", sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <IconUpload size={13} color="#0070f3" />
                      <span>{uploadingLogo ? 'Subiendo...' : 'Subir logo'}</span>
                    </button>
                    <input ref={logoInputRef} type="file" style={{ display: 'none' }} accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
                  </div>
                </div>

                <div>
                  <label style={glassLabelStyle}>Nombre de la Marca *</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Espacio Vital..." required style={glassInputStyle} />
                </div>

                <div>
                  <label style={glassLabelStyle}>Responsable Asignado</label>
                  <select value={form.responsableId} onChange={e => setForm(p => ({ ...p, responsableId: e.target.value }))} style={glassInputStyle}>
                    <option value="">Sin asignar</option>
                    {team.map((u: any) => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.role}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={glassLabelStyle}>Redes Sociales Activas</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {NETWORKS.map(n => {
                      const IconComponent = n.icon;
                      const active = form.networks[n.key as keyof typeof form.networks];
                      return (
                        <div
                          key={n.key}
                          onClick={() => setForm(p => ({ ...p, networks: { ...p.networks, [n.key]: !active } }))}
                          style={{
                            flex: 1,
                            padding: '12px 8px',
                            borderRadius: '12px',
                            border: `2px solid ${active ? n.color : 'rgba(226, 232, 240, 0.8)'}`,
                            background: active ? `${n.color}14` : 'rgba(255, 255, 255, 0.6)',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                            <IconComponent size={20} color={active ? n.color : '#64748b'} />
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: 800, color: active ? n.color : '#64748b' }}>
                            {n.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label style={glassLabelStyle}>Entregables Mensuales Comprometidos</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {PIECE_TYPES.map((pt, i) => (
                      <div
                        key={pt.key}
                        style={{
                          background: 'rgba(255, 255, 255, 0.65)',
                          borderRadius: '12px',
                          padding: '12px',
                          border: '1px solid rgba(226, 232, 240, 0.8)'
                        }}
                      >
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <AppleEmoji symbol={pt.icon} size={13} />
                          <span>{pt.label}</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={form.scopeItems[i]?.cantidad || 0}
                          onChange={e => setForm(p => ({ ...p, scopeItems: p.scopeItems.map((s, j) => j === i ? { ...s, cantidad: parseInt(e.target.value) || 0 } : s) }))}
                          style={{ ...glassInputStyle, padding: '8px', textAlign: 'center', fontSize: '16px', fontWeight: 800 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '28px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#0070f3',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Creando...' : 'Crear Marca'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    color: '#64748b',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL VENTANA GLASS: DETALLE DE MARCA */}
      {showDetail && selectedBrand && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.45)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowDetail(false); }}
        >
          <div
            className="liquid-glass-modal"
            style={{
              borderRadius: '24px',
              width: '100%',
              maxWidth: '660px',
              maxHeight: '88vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header del Modal de Detalle */}
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', gap: '16px', alignItems: 'center' }}>
              {selectedBrand.logo ? (
                <img src={selectedBrand.logo} alt={selectedBrand.name} style={{ width: '54px', height: '54px', borderRadius: '14px', objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.9)' }} />
              ) : (
                <div style={{ width: '54px', height: '54px', borderRadius: '14px', background: 'linear-gradient(135deg, rgba(0, 112, 243, 0.15), rgba(0, 112, 243, 0.05))', border: '1px solid rgba(0, 112, 243, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 900, color: '#0070f3', flexShrink: 0 }}>
                  {selectedBrand.name[0]?.toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#0f2a4a' }}>{selectedBrand.name}</h2>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                  {NETWORKS.filter(n => (selectedBrand.networks || {})[n.key]).map((n, idx) => {
                    const IconComponent = n.icon;
                    return (
                      <div
                        key={n.key}
                        className="social-glass-btn"
                        title={n.label}
                        style={{
                          color: n.color,
                          animationDelay: `${idx * 0.08}s`
                        }}
                      >
                        <IconComponent size={14} color={n.color} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  onClick={openEdit}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    color: '#0070f3',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <IconEdit size={13} color="#0070f3" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={handleDelete}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    background: 'rgba(239, 68, 68, 0.08)',
                    color: '#ef4444',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <IconTrash size={13} color="#ef4444" />
                  <span>Eliminar</span>
                </button>
                <button
                  onClick={() => setShowDetail(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    color: '#64748b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconX size={16} />
                </button>
              </div>
            </div>

            {/* Pestañas Glass */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', padding: '0 28px' }}>
              {[['info', 'Información'], ['scope', 'Entregables']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as any)}
                  style={{
                    padding: '14px 20px',
                    border: 'none',
                    borderBottom: activeTab === key ? '2px solid #0070f3' : '2px solid transparent',
                    background: 'transparent',
                    color: activeTab === key ? '#0070f3' : '#64748b',
                    fontWeight: activeTab === key ? 800 : 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    marginBottom: '-1px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Contenido de las Pestañas */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              {activeTab === 'info' && (() => {
                const tot = scopeItems.reduce((s, i) => s + i.cantidad, 0);
                const comp = scopeItems.reduce((s, i) => s + (i.completados || 0), 0);
                const pct = tot > 0 ? Math.round((comp / tot) * 100) : 0;
                const pctColor = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#0070f3';

                return (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                      {[
                        ['Responsable', selectedBrand.responsable ? `${selectedBrand.responsable.firstName} ${selectedBrand.responsable.lastName}` : 'Sin asignar'],
                        ['Estado', selectedBrand.active ? 'Activa' : 'Inactiva'],
                        ['Solicitudes', `${selectedBrand._count?.requests || 0}`],
                        ['Piezas Creadas', `${selectedBrand._count?.pieces || 0}`]
                      ].map(([k, v]) => (
                        <div key={k} style={{ background: 'rgba(255, 255, 255, 0.7)', borderRadius: '14px', padding: '14px 16px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                          <div style={{ fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '5px' }}>{k}</div>
                          <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f2a4a' }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    {tot > 0 && (
                      <div style={{ background: 'rgba(255, 255, 255, 0.75)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a', textTransform: 'capitalize' }}>Progreso {mesActual}</div>
                          <div style={{ fontSize: '24px', fontWeight: 900, color: pctColor }}>{pct}%</div>
                        </div>
                        <div style={{ background: 'rgba(226, 232, 240, 0.8)', borderRadius: '6px', height: '8px', overflow: 'hidden', marginBottom: '12px' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: '6px', transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px', fontWeight: 600 }}>
                          {selectedBrand._count?.pieces || 0} piezas creadas en la plataforma
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {scopeItems.filter(s => s.cantidad > 0).map((s: any) => {
                            const pt = PIECE_TYPES.find(p => p.key === s.tipo);
                            const p = Math.min(100, Math.round(((s.completados || 0) / s.cantidad) * 100));
                            return (
                              <div key={s.tipo} style={{ background: 'white', borderRadius: '10px', padding: '10px 12px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {pt?.icon && <AppleEmoji symbol={pt.icon} size={12} />}
                                    <span>{s.tipo}</span>
                                  </span>
                                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#0070f3' }}>{s.completados || 0}/{s.cantidad}</span>
                                </div>
                                <div style={{ background: 'rgba(226, 232, 240, 0.7)', borderRadius: '3px', height: '4px' }}>
                                  <div style={{ height: '100%', width: `${p}%`, background: '#0070f3', borderRadius: '3px' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {activeTab === 'scope' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                      Entregables para <strong style={{ color: '#0f2a4a', textTransform: 'capitalize' }}>{MONTH_NAMES[detailScopeMonth - 1]} {detailScopeYear}</strong> · El progreso se calcula desde piezas aprobadas.
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={detailScopeMonth}
                        onChange={e => {
                          const m = Number(e.target.value);
                          setDetailScopeMonth(m);
                          if (selectedBrand) loadScope(selectedBrand.id, m, detailScopeYear);
                        }}
                        style={{ ...glassInputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px', fontWeight: 700 }}
                      >
                        {MONTH_NAMES.map((name, idx) => (
                          <option key={idx} value={idx + 1}>{name}</option>
                        ))}
                      </select>
                      <select
                        value={detailScopeYear}
                        onChange={e => {
                          const y = Number(e.target.value);
                          setDetailScopeYear(y);
                          if (selectedBrand) loadScope(selectedBrand.id, detailScopeMonth, y);
                        }}
                        style={{ ...glassInputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px', fontWeight: 700 }}
                      >
                        {[2025, 2026, 2027].map(yr => (
                          <option key={yr} value={yr}>{yr}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '22px' }}>
                    {PIECE_TYPES.map(pt => {
                      const item = scopeItems.find(s => s.tipo === pt.key) || { tipo: pt.key, cantidad: 0, completados: 0 };
                      return (
                        <div key={pt.key} style={{ background: 'rgba(255, 255, 255, 0.7)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AppleEmoji symbol={pt.icon} size={14} />
                            <span>{pt.label}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px', fontWeight: 600 }}>
                            Aprobados: <strong style={{ color: '#10b981' }}>{item.completados || 0}</strong>
                          </div>
                          <label style={glassLabelStyle}>Cantidad Asignada</label>
                          <input
                            type="number"
                            min="0"
                            value={item.cantidad}
                            onChange={e => {
                              const val = parseInt(e.target.value) || 0;
                              setScopeItems(prev => {
                                const copy = [...prev];
                                const idx = copy.findIndex(s => s.tipo === pt.key);
                                if (idx >= 0) copy[idx] = { ...copy[idx], cantidad: val };
                                else copy.push({ tipo: pt.key, cantidad: val, completados: 0 });
                                return copy;
                              });
                            }}
                            style={{ ...glassInputStyle, textAlign: 'center', fontSize: '20px', fontWeight: 900, padding: '8px' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleSaveScope}
                    disabled={savingScope}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#0070f3',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)',
                      opacity: savingScope ? 0.7 : 1
                    }}
                  >
                    {savingScope ? 'Guardando...' : 'Guardar Entregables'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL VENTANA GLASS: EDITAR MARCA */}
      {showEdit && selectedBrand && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 42, 74, 0.45)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px'
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowEdit(false); }}
        >
          <div
            className="liquid-glass-modal"
            style={{
              borderRadius: '24px',
              padding: '32px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(0, 112, 243, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconEdit size={16} color="#0070f3" />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>Editar {selectedBrand.name}</h2>
              </div>
              <button
                onClick={() => setShowEdit(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  color: '#64748b',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconX size={16} />
              </button>
            </div>

            <form onSubmit={handleEdit}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={glassLabelStyle}>Logo de la Marca</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      onClick={() => editLogoRef.current?.click()}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        border: '2px dashed rgba(0, 112, 243, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        background: 'rgba(255, 255, 255, 0.8)',
                        flexShrink: 0
                      }}
                    >
                      {editForm.logo ? (
                        <img src={editForm.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <AppleEmoji symbol="📷" size={20} />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => editLogoRef.current?.click()}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '10px',
                        border: '1px solid rgba(203, 213, 225, 0.8)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#0f2a4a',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: '"Montserrat", sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <IconUpload size={13} color="#0070f3" />
                      <span>Cambiar logo</span>
                    </button>
                    <input ref={editLogoRef} type="file" style={{ display: 'none' }} accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0], true)} />
                  </div>
                </div>

                <div>
                  <label style={glassLabelStyle}>Nombre</label>
                  <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} required style={glassInputStyle} />
                </div>

                <div>
                  <label style={glassLabelStyle}>Responsable</label>
                  <select value={editForm.responsableId} onChange={e => setEditForm(p => ({ ...p, responsableId: e.target.value }))} style={glassInputStyle}>
                    <option value="">Sin asignar</option>
                    {team.map((u: any) => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.role}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={glassLabelStyle}>Redes Sociales</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {NETWORKS.map(n => {
                      const IconComponent = n.icon;
                      const active = editForm.networks[n.key as keyof typeof editForm.networks];
                      return (
                        <div
                          key={n.key}
                          onClick={() => setEditForm(p => ({ ...p, networks: { ...p.networks, [n.key]: !active } }))}
                          style={{
                            flex: 1,
                            padding: '10px 8px',
                            borderRadius: '12px',
                            border: `2px solid ${active ? n.color : 'rgba(226, 232, 240, 0.8)'}`,
                            background: active ? `${n.color}14` : 'rgba(255, 255, 255, 0.6)',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'center' }}>
                            <IconComponent size={18} color={active ? n.color : '#64748b'} />
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: 800, color: active ? n.color : '#64748b' }}>
                            {n.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: 'rgba(255, 255, 255, 0.7)', borderRadius: '12px', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                  <input type="checkbox" id="activeCheck" checked={editForm.active} onChange={e => setEditForm(p => ({ ...p, active: e.target.checked }))} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                  <label htmlFor="activeCheck" style={{ fontSize: '13px', fontWeight: 700, color: '#0f2a4a', cursor: 'pointer' }}>Marca activa</label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button
                  type="submit"
                  disabled={savingEdit}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#0070f3',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)',
                    opacity: savingEdit ? 0.7 : 1
                  }}
                >
                  {savingEdit ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEdit(false)}
                  style={{
                    padding: '12px 18px',
                    borderRadius: '12px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    color: '#64748b',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;
